import React, { useEffect, useState } from 'react';

interface ServiceStatus {
  postgres: {
    running: boolean;
    pid?: number;
    port?: number;
  };
  backend: {
    running: boolean;
    pid?: number;
    port?: number;
  };
}

const ServiceStatusIndicator: React.FC = () => {
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [isElectron, setIsElectron] = useState(false);

  useEffect(() => {
    // Check if we're running in Electron
    const electronAPI = (window as any).electronAPI;
    setIsElectron(!!electronAPI);

    if (!electronAPI) return;

    // Fetch status every 5 seconds
    const fetchStatus = async () => {
      try {
        const serviceStatus = await electronAPI.getServiceStatus();
        setStatus(serviceStatus);
      } catch (error) {
        console.error('Failed to fetch service status:', error);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  // Don't render if not in Electron mode
  if (!isElectron) return null;

  const getStatusColor = (running: boolean) => {
    return running ? 'bg-green-500' : 'bg-red-500';
  };

  const getStatusText = (running: boolean) => {
    return running ? 'Running' : 'Stopped';
  };

  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 border border-gray-200">
      <div className="text-xs font-semibold text-gray-700 mb-2">Service Status</div>

      {/* PostgreSQL Status */}
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-2 h-2 rounded-full ${status ? getStatusColor(status.postgres.running) : 'bg-gray-400'}`}></div>
        <span className="text-xs text-gray-600">PostgreSQL</span>
        {status?.postgres.running && status.postgres.port && (
          <span className="text-xs text-gray-400">:{status.postgres.port}</span>
        )}
      </div>

      {/* Backend Status */}
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${status ? getStatusColor(status.backend.running) : 'bg-gray-400'}`}></div>
        <span className="text-xs text-gray-600">Backend</span>
        {status?.backend.running && status.backend.port && (
          <span className="text-xs text-gray-400">:{status.backend.port}</span>
        )}
      </div>

      {/* Restart button if any service is down */}
      {status && (!status.postgres.running || !status.backend.running) && (
        <button
          onClick={async () => {
            const electronAPI = (window as any).electronAPI;
            if (electronAPI) {
              try {
                await electronAPI.restartServices();
              } catch (error) {
                console.error('Failed to restart services:', error);
              }
            }
          }}
          className="mt-2 w-full text-xs bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 rounded"
        >
          Restart Services
        </button>
      )}
    </div>
  );
};

export default ServiceStatusIndicator;
